import type { CustomControlCode } from '@common/enums';
import type { Field, Section, Subsection } from '@common/formEngine/formEngine.types';
import type { ProcessActivityContextData } from '@common/formEngine/processActivityContext.types';
import { deepPick } from '@common/utils/deepPickUtils';

export function shouldIncludeField<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    field: Field<Data, ContentKey, TCustomControlCode, Version>,
    processActivityContextData: ProcessActivityContextData<Data, TCustomControlCode, Version>
): boolean {
    return (
        !('isReadonly' in field && field.isReadonly) &&
        (field.include?.fn(deepPick(processActivityContextData, field.include.fieldKeys)) ?? true)
    );
}

export function shouldIncludeSubsection<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    subsection: Subsection<Data, ContentKey, TCustomControlCode, Version>,
    processActivityContextData: ProcessActivityContextData<Data, TCustomControlCode, Version>
): boolean {
    return (
        (subsection.include?.fn(deepPick(processActivityContextData, subsection.include.fieldKeys)) ?? true) &&
        subsection.fields.some((field) => shouldIncludeField(field, processActivityContextData))
    );
}

export function shouldIncludeSection<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(
    section: Section<Data, ContentKey, TCustomControlCode, Version>,
    processActivityContextData: ProcessActivityContextData<Data, TCustomControlCode, Version>
): boolean {
    return Boolean(
        (section.include?.fn(deepPick(processActivityContextData, section.include.fieldKeys)) ?? true) &&
            (section.fields.some((field) => shouldIncludeField(field, processActivityContextData)) ||
                section.subsections?.some((subsection) =>
                    shouldIncludeSubsection(subsection, processActivityContextData)
                ))
    );
}
